import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

type PeriodFilterProps = {
  from: Date;
  to: Date;
  onChange: (from: Date, to: Date) => void;
};

type YM = { y: number; m: number }; // month: 0-11

const months = [
  '1월',
  '2월',
  '3월',
  '4월',
  '5월',
  '6월',
  '7월',
  '8월',
  '9월',
  '10월',
  '11월',
  '12월',
];

export const PeriodFilter = ({ from, to, onChange }: PeriodFilterProps) => {
  const now = new Date();
  const [isYearMode, setIsYearMode] = useState(false);
  const [tempYear, setTempYear] = useState(from.getFullYear());

  const [draftStart, setDraftStart] = useState<YM | null>({
    y: from.getFullYear(),
    m: from.getMonth(),
  });
  const [draftEnd, setDraftEnd] = useState<YM | null>({
    y: to.getFullYear(),
    m: to.getMonth(),
  });

  const ymToKey = (ym: YM) => ym.y * 12 + ym.m;
  const nowYM: YM = { y: now.getFullYear(), m: now.getMonth() };
  const isFuture = (ym: YM) => ymToKey(ym) > ymToKey(nowYM);

  const prevYear = () => setTempYear((y) => y - 1);
  const nextYear = () => setTempYear((y) => Math.min(y + 1, now.getFullYear())); // 미래 연도 방지

  const getMonthStart = (y: number, m: number) => new Date(y, m, 1);
  const getMonthEnd = (y: number, m: number) => new Date(y, m + 1, 0);
  const applyRange = (y1: number, m1: number, y2: number, m2: number) => {
    onChange(getMonthStart(y1, m1), getMonthEnd(y2, m2));
  };

  const applySingleMonth = (y: number, m: number) => {
    setDraftStart({ y, m });
    setDraftEnd(null);
    applyRange(y, m, y, m);
  };

  const handleYearSelect = (year: number) => {
    setTempYear(year);
    // applyRange(year, 0, year, 11);
    const endMonth = year === now.getFullYear() ? now.getMonth() : 11; // 올해면 현재 달까지만
    applyRange(year, 0, year, endMonth);
  };

  // 특정 달이 선택/범위 내인지 계산
  const isSelected = (y: number, m: number) => {
    const k = ymToKey({ y, m });
    const s = draftStart ? ymToKey(draftStart) : null;
    const e = draftEnd ? ymToKey(draftEnd) : s;
    return s !== null && e !== null && k >= s && k <= e;
  };

  const isStart = (y: number, m: number) =>
    draftStart && draftStart.y === y && draftStart.m === m;
  const isEnd = (y: number, m: number) =>
    draftEnd && draftEnd.y === y && draftEnd.m === m;

  // 월 클릭 핸들러: 두 번 클릭로 범위 선택
  const handleMonthClick = (idx: number) => {
    const clicked: YM = { y: tempYear, m: idx };
    if (isFuture(clicked)) return;

    if (!draftStart || draftEnd) {
      applySingleMonth(clicked.y, clicked.m);
    } else {
      if (ymToKey(clicked) < ymToKey(draftStart)) {
        applySingleMonth(clicked.y, clicked.m);
      } else {
        setDraftEnd(clicked);
        applyRange(draftStart.y, draftStart.m, clicked.y, clicked.m);
      }
    }
  };

  const yearGridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isYearMode || !yearGridRef.current) return;
    const el = yearGridRef.current.querySelector<HTMLButtonElement>(
      `[data-year="${tempYear}"]`,
    );
    el?.scrollIntoView({ block: 'center' });
  }, [isYearMode, tempYear]);

  return (
    <Container>
      <Header>
        <YearToggle onClick={() => setIsYearMode(!isYearMode)}>
          {tempYear}년
          <ArrowButton>
            <ArrowIcon
              src="/assets/images/arrow/arrow-right-tertiary.svg"
              $rotate={isYearMode ? -90 : 90}
            />
          </ArrowButton>
        </YearToggle>
        <NavButtonWrapper>
          <NavButton onClick={prevYear}>
            <ArrowIcon
              src="/assets/images/arrow/arrow-right-tertiary.svg"
              $rotate={180}
            />
          </NavButton>
          <NavButton
            onClick={nextYear}
            disabled={tempYear >= now.getFullYear()}
          >
            <ArrowIcon src="/assets/images/arrow/arrow-right-tertiary.svg" />
          </NavButton>
        </NavButtonWrapper>
      </Header>

      {isYearMode ? (
        <YearContent>
          <YearGrid ref={yearGridRef}>
            {Array.from(
              { length: now.getFullYear() - 2000 + 1 },
              (_, i) => 2000 + i,
            ).map((y) => (
              <YearButton
                key={y}
                data-year={y}
                selected={y === tempYear}
                onClick={() => handleYearSelect(y)}
              >
                {y}년
              </YearButton>
            ))}
          </YearGrid>
        </YearContent>
      ) : (
        <MonthGrid>
          {months.map((m, idx) => {
            const ym = { y: tempYear, m: idx };
            const disabled = isFuture(ym);
            const selected = isSelected(tempYear, idx);
            return (
              <MonthButton
                key={idx}
                selected={selected}
                data-start={isStart(tempYear, idx) || undefined}
                data-end={isEnd(tempYear, idx) || undefined}
                disabled={disabled}
                onClick={() => handleMonthClick(idx)}
              >
                {m}
              </MonthButton>
            );
          })}
        </MonthGrid>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  padding: 8px 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const YearToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  color: #424854;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.32px;
`;

const NavButtonWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const NavButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const ArrowButton = styled.div`
  display: flex;
  width: 32px;
  height: 32px;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  & > img {
    width: 24px;
    height: 24px;
  }
`;

const ArrowIcon = styled.img<{ $rotate?: number }>`
  width: 20px;
  height: 20px;
  transform: rotate(${({ $rotate }) => $rotate || 0}deg);
`;

const YearContent = styled.div`
  display: flex;
  flex: 1 0 0;
  align-self: stretch;
  height: 240px;
  justify-content: center;
`;

const YearGrid = styled.div`
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding-bottom: 22px;
  &::-webkit-scrollbar {
    width: 0;
    background: transparent;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const YearButton = styled.button<{ selected?: boolean }>`
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: -2%;
  text-align: center;
  padding: 10px;
  border: none;
  background-color: #fff;
  color: ${({ selected }) => (selected ? '#3F6EF1' : '#A7ADB8')};
  cursor: pointer;
  position: relative;
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    width: ${({ selected }) => (selected ? '80px' : '0')};
    height: 1px;
    background-color: #a7adb88c;
    transform: translateX(-50%);
  }
  &::before {
    top: 0;
  }
  &::after {
    bottom: 0;
  }
`;

const MonthGrid = styled.div`
  display: grid;
  height: 240px;
  row-gap: 8px;
  column-gap: 4px;
  align-self: stretch;
  grid-template-rows: repeat(3, minmax(0, 1fr));
  grid-template-columns: repeat(4, minmax(0, 1fr));
`;

// 선택 범위 하이라이트(시작/끝/중간)
const MonthButton = styled.button<{ selected?: boolean }>`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -2%;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 77px;
  height: 74.67px;
  padding: 10px;
  border-radius: 6px;
  border: ${({ selected }) => (selected ? '1px solid #E4EDFD' : 'none')};
  background-color: ${({ selected }) => (selected ? '#E4EDFD' : '#fff')};
  color: ${({ selected }) => (selected ? '#3F6EF1' : '#A7ADB8')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  position: relative;

  &[data-start='true'] {
    border: 1px solid #3f6ef1;
    box-shadow: inset 2px 0 0 #cfe0ff;
  }
  &[data-end='true'] {
    border: 1px solid #3f6ef1;
    box-shadow: inset -2px 0 0 #cfe0ff;
  }
`;
